import type { AxiosError } from 'axios';
import { toast } from '~/composables/useToast';

/** DisplayApiException / DisplayException: a flat list of JSONAPI-ish error objects. */
interface JsonApiError {
    code?: string;
    detail?: string;
    /** Present on validation failures -- the request field the message belongs to. */
    source?: { field?: string };
}

/**
 * The two error bodies the backend actually produces: the JSONAPI array above,
 * and Laravel's default validation shape (`errors` keyed by field name). Both
 * reach the generated client, so both have to be understood -- the legacy
 * RequestService handled the pair in its 400/422 branch.
 */
interface ApiErrorBody {
    errors?: JsonApiError[] | Record<string, string[]>;
    message?: string;
}

/**
 * The failure body, whatever axios made of it. Endpoints Scribe documented
 * without a JSON response example are generated with `responseType: 'text'`,
 * and axios honours that for error responses too -- so the 422 body arrives as
 * an unparsed JSON string rather than an object. Parse it back, or every
 * validation failure on those endpoints degrades to the generic toast with no
 * inline field errors.
 */
function errorBody(error: unknown): ApiErrorBody | undefined {
    const data = (error as AxiosError<ApiErrorBody | string>)?.response?.data;

    if (typeof data !== 'string') return data;

    try {
        return JSON.parse(data) as ApiErrorBody;
    } catch {
        return undefined;
    }
}

/** Every human-readable message in the body, in the order the backend listed them. */
function errorMessages(body: ApiErrorBody | undefined): string[] {
    const errors = body?.errors;

    if (Array.isArray(errors)) {
        return errors.map((error) => error.detail).filter((detail): detail is string => !!detail);
    }

    if (errors) {
        return Object.values(errors)
            .flat()
            .filter((message): message is string => typeof message === 'string');
    }

    return body?.message ? [body.message] : [];
}

/**
 * Toast an error from a generated-client (axios) call. Surfaces the backend's
 * own messages when it sent any, otherwise the generic failure message -- the
 * counterpart of what useService's displayErrorsInUI did for the legacy
 * service layer.
 */
export function toastApiError(error: unknown): void {
    const messages = errorMessages(errorBody(error));

    if (messages.length > 1) {
        return toast.error(
            ['components.form.validation_error', messages.length],
            messages.map((message) => ['_raw', message] as TranslatableMessage),
        );
    }

    toast.error(messages[0] ? ['_raw', messages[0]] : ['generic.something_went_wrong']);
}

/**
 * The same error split per field, keyed by the request field name -- which is
 * also the `name` a form component registers itself under, so `<v-form>` can
 * drop each message back onto the input that caused it. Empty for failures the
 * backend didn't attribute to a field (a 500, a DisplayException).
 */
export function apiFieldErrors(error: unknown): Record<string, string[]> {
    const errors = errorBody(error)?.errors;
    const fields: Record<string, string[]> = {};

    if (Array.isArray(errors)) {
        for (const entry of errors) {
            const field = entry.source?.field;
            if (!field || !entry.detail) continue;

            fields[field] = [...(fields[field] ?? []), entry.detail];
        }

        return fields;
    }

    for (const [field, messages] of Object.entries(errors ?? {})) {
        const list = [messages].flat().filter((message): message is string => typeof message === 'string');
        if (list.length) fields[field] = list;
    }

    return fields;
}
